import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import GroupedBarChart from "@/components/admin/GroupedBarChart";
import {
  Users, LifeBuoy, Euro, Crown, UserCheck, Sparkles, Loader2, Send,
  UtensilsCrossed, Dumbbell, Video, UserPlus, Star, ChevronRight,
} from "lucide-react";

function StatCard({ label, value, icon: Icon, color, loading }) {
  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="space-y-2"><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-16" /></div>
            <Skeleton className="w-11 h-11 rounded-xl" />
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const ACTIVITY_COLORS = {
  blue: "bg-blue-100 text-blue-600",
  orange: "bg-orange-100 text-orange-600",
  purple: "bg-purple-100 text-purple-600",
};

const QUICK_LINKS = [
  { label: "Utilisateurs", path: "/admin/users", color: "text-blue-600" },
  { label: "Support", path: "/admin/support", color: "text-orange-600" },
  { label: "Revenus", path: "/admin/revenue", color: "text-emerald-600" },
  { label: "Contenu", path: "/admin/content", color: "text-indigo-600" },
  { label: "Communications", path: "/admin/communications", color: "text-purple-600" },
  { label: "Système", path: "/admin/system", color: "text-slate-600" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/charts"),
      api.get("/admin/activity"),
    ])
      .then(([statsRes, chartsRes, activityRes]) => {
        setStats(statsRes.data);
        setCharts(chartsRes.data);
        setActivity(activityRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAnalytics = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || analyticsLoading) return;
    setAnalyticsLoading(true);
    setAnswer("");
    try {
      const { data } = await api.post("/ai/admin-analytics", { question: q });
      setAnswer(data.answer);
    } catch {
      setAnswer("Erreur lors de l'analyse. Réessayez.");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const suggestions = [
    "Quel est le taux de conversion ?",
    "Revenus ce mois ?",
    "Coachs les plus actifs ?",
  ];

  const statCards = stats ? [
    { label: "Coachs", value: stats.coaches, icon: UserCheck, color: "bg-blue-50 text-blue-600" },
    { label: "Élèves", value: stats.students, icon: Users, color: "bg-green-50 text-green-600" },
    { label: "Abonnements actifs", value: stats.active_subscriptions, icon: Crown, color: "bg-purple-50 text-purple-600" },
    { label: "Tickets ouverts", value: stats.open_tickets, icon: LifeBuoy, color: "bg-orange-50 text-orange-600" },
    { label: "Revenus plateforme", value: `${stats.total_revenue?.toFixed(0) ?? 0} €`, icon: Euro, color: "bg-emerald-50 text-emerald-600" },
    { label: "Nouveaux (7j)", value: stats.new_users_week, icon: UserPlus, color: "bg-cyan-50 text-cyan-600" },
    { label: "Plans alimentaires", value: stats.total_diet_plans, icon: UtensilsCrossed, color: "bg-amber-50 text-amber-600" },
    { label: "Programmes", value: stats.total_training_plans, icon: Dumbbell, color: "bg-rose-50 text-rose-600" },
    { label: "Vidéos", value: stats.total_videos, icon: Video, color: "bg-indigo-50 text-indigo-600" },
  ] : [];

  return (
    <div>
      <AdminPageHeader
        title="Dashboard"
        subtitle="Vue d'ensemble et télémétrie de la plateforme"
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 9 }).map((_, i) => <StatCard key={i} loading />)
          : statCards.map((c, i) => <StatCard key={i} {...c} loading={false} />)}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-1">Croissance utilisateurs (12 mois)</h2>
            <p className="text-xs text-slate-400 mb-4">Inscriptions coachs et élèves</p>
            {loading ? (
              <Skeleton className="h-40 w-full rounded-xl" />
            ) : (
              <>
                <GroupedBarChart
                  data={charts?.user_growth}
                  keys={["coaches", "students"]}
                  colors={["bg-blue-500", "bg-green-500"]}
                />
                <div className="flex gap-4 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Coachs</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" /> Élèves</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Accès rapides</h2>
            <div className="space-y-1">
              {QUICK_LINKS.map((link) => (
                <button
                  key={link.path}
                  type="button"
                  onClick={() => navigate(link.path)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors text-left group"
                >
                  <span className={`text-sm font-medium ${link.color}`}>{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" /> Avis & plans créés
            </h2>
            <p className="text-xs text-slate-400 mb-4">Activité mensuelle</p>
            {loading ? (
              <Skeleton className="h-32 w-full rounded-xl" />
            ) : (
              <GroupedBarChart
                data={charts?.plans_chart?.map((p, i) => ({
                  month: p.month,
                  avis: charts.reviews_chart?.[i]?.avis ?? 0,
                  plans: (p.diet ?? 0) + (p.entrainement ?? 0),
                }))}
                keys={["avis", "plans"]}
                colors={["bg-amber-400", "bg-violet-500"]}
                height={120}
              />
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Activité récente</h2>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
              </div>
            ) : activity.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Aucune activité récente</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activity.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ACTIVITY_COLORS[item.color] ?? ACTIVITY_COLORS.blue}`}>
                      {item.type === "ticket" ? <LifeBuoy className="w-4 h-4" /> :
                       item.type === "subscription" ? <Crown className="w-4 h-4" /> :
                       <UserPlus className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.label}</p>
                      <p className="text-xs text-slate-400 truncate">{item.meta}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {item.date ? new Date(item.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 border-purple-200 bg-gradient-to-r from-purple-50/50 to-indigo-50/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Analytics IA</h2>
              <p className="text-xs text-slate-400">Interrogez vos données en langage naturel</p>
            </div>
          </div>

          <form onSubmit={handleAnalytics} className="flex gap-2">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ex: Combien de coachs Pro ce mois ?"
              className="flex-1 h-11 rounded-xl border border-purple-200 bg-white px-4 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              disabled={analyticsLoading}
              maxLength={500}
            />
            <Button type="submit" disabled={analyticsLoading || !question.trim()} className="bg-purple-600 hover:bg-purple-700 rounded-xl h-11 px-5">
              {analyticsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>

          <div className="flex flex-wrap gap-2 mt-3">
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => setQuestion(s)} className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200">
                {s}
              </button>
            ))}
          </div>

          {answer && (
            <div className="mt-4 bg-white rounded-xl border border-purple-100 p-4">
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{answer}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}